import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  activeStudyInstanceUIDs = [],
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
  onEditDescription = noop,
  quizStatusMap = {},
  viewedStudyUIDs = [],
}: withAppTypes) => {
  const listRef = useRef<HTMLDivElement>(null);
  // `${activeTabName}:${uid}` of the last auto-scroll, so we scroll once per
  // tab entry / study change rather than on every thumbnail/display-set update.
  const scrolledKeyRef = useRef<string | null>(null);

  const activeTabStudies = tabs.find(tab => tab.name === activeTabName)?.studies;
  const activeUidSignature = activeStudyInstanceUIDs.join(',');

  useEffect(() => {
    scrolledKeyRef.current = null;
  }, [activeTabName]);

  // Bring the currently open study into view (e.g. when switching to the
  // Worklist tab, whose rows arrive asynchronously after the toggle).
  useEffect(() => {
    const activeUid = activeStudyInstanceUIDs.find(uid =>
      activeTabStudies?.some(study => study.studyInstanceUid === uid)
    );
    if (!activeUid) {
      return;
    }
    const scrollKey = `${activeTabName}:${activeUid}`;
    if (scrolledKeyRef.current === scrollKey) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-study-uid="${activeUid}"]`);
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        scrolledKeyRef.current = scrollKey;
      }
    });
    return () => cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName, activeUidSignature, activeTabStudies?.length]);

  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets, patientName }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        const isActive = activeStudyInstanceUIDs.includes(studyInstanceUid);
        const quizStatus = quizStatusMap[studyInstanceUid];
        // A study is considered viewed if user answered at least one question OR explicitly viewed
        const isViewed = (quizStatus?.answeredCount > 0) || viewedStudyUIDs.includes(studyInstanceUid);
        return (
          <div
            key={studyInstanceUid}
            data-study-uid={studyInstanceUid}
          >
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              patientName={patientName}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isActive}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
              onEditDescription={onEditDescription}
              quizStatus={quizStatus}
              isViewed={isViewed}
            />
          </div>
        );
      }
    );
  };

  return (
    <ScrollArea type="always">
      <div
        className="bg-bkg-low flex flex-1 flex-col gap-[4px] pr-4"
        data-cy={'studyBrowser-panel'}
      >
        <div
          ref={listRef}
          className="flex flex-col gap-[4px]"
        >
          {showSettings && (
            <div className="w-100 bg-bkg-low flex h-[48px] items-center justify-center gap-[10px] px-[8px] py-[10px]">
              <>
                <StudyBrowserViewOptions
                  tabs={tabs}
                  onSelectTab={onClickTab}
                  activeTabName={activeTabName}
                />
                <StudyBrowserSort servicesManager={servicesManager} />
              </>
            </div>
          )}
          {getTabContent()}
        </div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
  quizStatusMap: PropTypes.object,
  viewedStudyUIDs: PropTypes.arrayOf(PropTypes.string),
};

export { StudyBrowser };
